import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress
} from "@mui/material";
import ReservationPromptDialog from "../../../components/ReservationPromptDialog/reservationPrompt";

const OrderNotesDialog = ({ 
    open, 
    onClose, 
    onSave, 
    selectedOrder,
    loading = false,
    isTabsPos = false,
    isCallCenterUser = false,
    isExistingDraft = false,
    reservedDesignatedNumbers = []
}) => {
    const { t } = useTranslation();
    const [notes, setNotes] = useState('');
    const [diners, setDiners] = useState('');

    useEffect(() => {
        if (selectedOrder && open) {
            setNotes(selectedOrder.notes || '');
            setDiners(selectedOrder.dinners || '');
        }
    }, [selectedOrder, open]);

    const handleSave = () => {
        onSave({
            notes: notes,
            dinners: parseInt(diners) || 0
        });
    };

    const handleCancel = () => {
        // Reset to original values
        setNotes(selectedOrder?.notes || '');
        setDiners(selectedOrder?.dinners || '');
        onClose();
    };

    const isValidForm = () => {
        return diners === '' || (!isNaN(parseInt(diners)) && parseInt(diners) >= 0);
    };

    if (isTabsPos) {
        if (!open) return null;
        return (
            <ReservationPromptDialog
                onResolve={(formData) => {
                    if (!formData) {
                        onClose?.();
                        return;
                    }
                    onSave?.(formData);
                }}
                onReject={() => {
                    onClose?.();
                }}
                options={{
                    title: t("pos.orderNotesTitle"),
                    message: t("pos.orderNotesPrompt"),
                    confirmText: t("common.save"),
                    cancelText: t("common.cancel"),
                    isTabsPos: true,
                    isCallCenterUser,
                    showSaveDraftButton: false,
                    reservedDesignatedNumbers,
                    initialData: {
                        notes: selectedOrder?.notes || "",
                        estimated_preparation_time:
                            selectedOrder?.estimated_preparation_time || 15,
                        designated_number: selectedOrder?.designated_number || "",
                        customer: selectedOrder?.customer || "",
                        mobile_no: selectedOrder?.mobile_no || "",
                        address_line1: selectedOrder?.address_line1 || "",
                        address_line2: selectedOrder?.address_line2 || "",
                        delivery_provider: selectedOrder?.delivery_provider || "",
                        use_delivery_provider: !!selectedOrder?.delivery_provider,
                        is_existing_draft: isExistingDraft,
                    },
                }}
            />
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={loading ? undefined : handleCancel}
            maxWidth="sm" 
            fullWidth
        >
            <DialogTitle>{t("pos.editOrderDetails")}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label={t("pos.orderNotes")}
                        multiline
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("pos.orderNotesPlaceholder")}
                        fullWidth
                        disabled={loading}
                    />
                    <TextField
                        label={t("pos.numberOfDiners")}
                        type="number"
                        value={diners}
                        onChange={(e) => setDiners(e.target.value)}
                        placeholder={t("pos.numberOfDinersPlaceholder")}
                        fullWidth
                        disabled={loading}
                        inputProps={{ min: 0 }}
                        error={!isValidForm()}
                        helperText={!isValidForm() ? t("pos.validDiners") : ""}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={handleCancel}
                    variant="outlined"
                    disabled={loading}
                >
                    {t("common.cancel")}
                </Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={loading || !isValidForm()}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {loading ? t("common.saving") : t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderNotesDialog; 